import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { getOrganization, updateOrganization, getOrganizationUsers, updateUserBypassPermission } from '../api/api';
import { PageContainer, Card, Button, Input } from '../components/ui';

export default function Organization() {
  const { organizationId } = useParams();
  const { user } = useAuth();
  const { t } = useLanguage();
  const [org, setOrg] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [togglingUserId, setTogglingUserId] = useState(null);
  const isAdmin = user?.role === 'ADMIN';
  const [form, setForm] = useState({
    name: '',
    employeesCount: '',
    hasItTeam: false,
    hasSecurityPolicy: false,
  });

  useEffect(() => {
    const load = async () => {
      try {
        const data = await getOrganization(organizationId);
        setOrg(data);
        setForm({
          name: data.name || '',
          employeesCount: data.employeesCount ?? '',
          hasItTeam: data.hasItTeam ?? false,
          hasSecurityPolicy: data.hasSecurityPolicy ?? false,
        });
        if (user?.role === 'ADMIN') {
          const userList = await getOrganizationUsers(organizationId);
          setUsers(userList);
        }
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [organizationId, user?.role]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      await updateOrganization(organizationId, {
        name: form.name,
        employeesCount: form.employeesCount ? Number(form.employeesCount) : null,
        hasItTeam: form.hasItTeam,
        hasSecurityPolicy: form.hasSecurityPolicy,
      });
      setOrg({ ...org, ...form });
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleBypassToggle = async (u, enabled) => {
    if (u.id === user?.id) return; // don't toggle self via this UI (admin always has bypass)
    setTogglingUserId(u.id);
    setError('');
    try {
      await updateUserBypassPermission(u.id, enabled);
      setUsers((prev) =>
        prev.map((x) => (x.id === u.id ? { ...x, canBypassPhishingVerification: enabled } : x))
      );
    } catch (err) {
      setError(err.message);
    } finally {
      setTogglingUserId(null);
    }
  };

  if (loading) {
    return (
      <Layout>
        <PageContainer>
          <div className="flex justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0ea5e9]" />
          </div>
        </PageContainer>
      </Layout>
    );
  }

  if (error && !org) {
    return (
      <Layout>
        <PageContainer>
          <div className="bg-red-500/10 text-red-600 dark:text-red-400 border border-red-500/30 p-4 rounded-[10px]">{error}</div>
        </PageContainer>
      </Layout>
    );
  }

  return (
    <Layout>
      <PageContainer>
        <h1 className="text-2xl font-bold text-[#0f172a] dark:text-slate-100 mb-8">{t.organizationData}</h1>

        {error && (
          <div className="bg-red-500/10 text-red-600 dark:text-red-400 border border-red-500/30 p-4 rounded-[10px] mb-6">{error}</div>
        )}

        <form onSubmit={handleSubmit} className="max-w-xl">
          <Card className="p-6 space-y-6">
            <div>
              <label className="block text-sm font-medium text-[#0f172a] dark:text-slate-300 mb-1">{t.name}</label>
              <Input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#0f172a] dark:text-slate-300 mb-1">{t.employeesCount}</label>
              <Input
                type="number"
                min="1"
                value={form.employeesCount}
                onChange={(e) => setForm({ ...form, employeesCount: e.target.value })}
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="hasItTeam"
                checked={form.hasItTeam}
                onChange={(e) => setForm({ ...form, hasItTeam: e.target.checked })}
                className="w-4 h-4 accent-[#0ea5e9] rounded"
              />
              <label htmlFor="hasItTeam" className="text-[#0f172a] dark:text-slate-300">{t.hasItTeam}</label>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="hasSecurityPolicy"
                checked={form.hasSecurityPolicy}
                onChange={(e) => setForm({ ...form, hasSecurityPolicy: e.target.checked })}
                className="w-4 h-4 accent-[#0ea5e9] rounded"
              />
              <label htmlFor="hasSecurityPolicy" className="text-[#0f172a] dark:text-slate-300">{t.hasSecurityPolicy}</label>
            </div>
            <Button type="submit" variant="primary" disabled={saving}>
              {saving ? t.saving : t.save}
            </Button>
          </Card>
        </form>

        {isAdmin && (
          <div className="mt-10">
            <h2 className="text-xl font-bold text-[#0f172a] dark:text-slate-100 mb-4">
              {t.organizationUsers}
            </h2>
            <Card className="p-6 overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-slate-200 dark:border-slate-600">
                  <th className="pb-3 text-sm font-medium text-slate-600 dark:text-slate-400">{t.name}</th>
                  <th className="pb-3 text-sm font-medium text-slate-600 dark:text-slate-400">{t.email}</th>
                  <th className="pb-3 text-sm font-medium text-slate-600 dark:text-slate-400">{t.userRole}</th>
                  <th className="pb-3 text-sm font-medium text-slate-600 dark:text-slate-400">{t.bypassPermission}</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-b border-slate-100 dark:border-slate-700 last:border-0">
                    <td className="py-3 text-slate-800 dark:text-slate-200">{u.name}</td>
                    <td className="py-3 text-slate-600 dark:text-slate-300">{u.email}</td>
                    <td className="py-3 text-slate-600 dark:text-slate-300">{u.role || 'USER'}</td>
                    <td className="py-3">
                      {u.role === 'ADMIN' ? (
                        <span className="text-sm text-slate-500 dark:text-slate-400">
                          {t.bypassPermission} ({t.bypassAlways})
                        </span>
                      ) : (
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={!!u.canBypassPhishingVerification}
                            onChange={(e) => handleBypassToggle(u, e.target.checked)}
                            disabled={togglingUserId === u.id}
                            className="h-4 w-4 rounded accent-[#0ea5e9]"
                          />
                          <span className="text-sm text-slate-600 dark:text-slate-300">
                            {togglingUserId === u.id
                              ? (u.canBypassPhishingVerification ? t.revokingBypass : t.grantingBypass)
                              : null}
                          </span>
                        </label>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </Card>
          </div>
        )}
      </PageContainer>
    </Layout>
  );
}
